import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as mammoth from 'mammoth';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PDFDocument } from 'pdf-lib';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { callClaude } from '../utils/claudeProxy';
import {
  collection, getDocs, addDoc, doc, getDoc, updateDoc, deleteDoc,
  query, orderBy
} from 'firebase/firestore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HandshakeIcon from '@mui/icons-material/Handshake';
import FolderIcon from '@mui/icons-material/Folder';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, Divider, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Alert, LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GavelIcon from '@mui/icons-material/Gavel';
import VerifiedIcon from '@mui/icons-material/Verified';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import LagbasLogo from '../components/LagbasLogo';

// Anställningsavtal hanteras inte i LagbasN (de finns som filer på servern).
// Reglerna nekar att avtal av typen Anställning skapas.
const CONTRACT_TYPES = ['Kund', 'Leverantör', 'Partner', 'Konsultavtal', 'Övrigt'];

// Brickor för snabbstart – klicka för att öppna formulär med förval
const TYPE_TILES = [
  {
    type: 'Konsultavtal',
    icon: AssignmentTurnedInIcon,
    color: '#0277BD', bg: '#e1f5fe', border: '#0277BD',
    desc: 'Konsultavtal kopplat till uppdrag (ABK 09)',
    featured: true,
  },
  {
    type: 'Kund',
    icon: BusinessCenterIcon,
    color: '#1565C0', bg: '#e3f2fd', border: '#1565C0',
    desc: 'Konsult- eller kundavtal',
  },
  {
    type: 'Leverantör',
    icon: LocalShippingIcon,
    color: '#2E7D32', bg: '#E8F5E9', border: '#2E7D32',
    desc: 'Leverantörs- eller inköpsavtal',
  },
  {
    type: 'Partner',
    icon: HandshakeIcon,
    color: '#6a1b9a', bg: '#f3e5f5', border: '#6a1b9a',
    desc: 'Samarbetsavtal eller partneravtal',
  },
  {
    type: 'Övrigt',
    icon: FolderIcon,
    color: '#455A64', bg: '#ECEFF1', border: '#90a4ae',
    desc: 'Annat avtal',
  },
];
const CATEGORIES     = ['Konsulting', 'Produkt / licens', 'SaaS', 'Tjänst', 'Övrigt'];
const STATUSES = [
  { key: 'draft',    label: 'Utkast',              color: '#757575', bg: '#f5f5f5' },
  { key: 'active',   label: 'Aktivt',              color: '#2E7D32', bg: '#E8F5E9' },
  { key: 'expiring', label: 'Förfaller snart',     color: '#E65100', bg: '#FFF3E0' },
  { key: 'expired',  label: 'Utgånget',            color: '#C62828', bg: '#FFEBEE' },
  { key: 'cancelled',label: 'Uppsagt / avslutat',  color: '#455A64', bg: '#ECEFF1' },
];

const EMPTY_FORM = {
  title: '', contractType: '', category: '', status: 'active',
  counterpartyName: '', counterpartyId: '',
  startDate: '', endDate: '', autoRenewal: false,
  noticePeriod: '', value: '', currency: 'SEK',
  paymentTerms: '', signedDate: '', signedBy: '',
  reminderDays: 60,
  notes: '', fileUrl: '', files: [],
  // Consultant contract specific
  projectName: '',
  projectNumber: '',
  consultantRole: '',
  hourlyRate: '',
  compensationType: 'rörligt',
  standardTerms: 'ABK 09',
  indexClause: '',
  invoiceReference: '',
  contactPersonClient: '',
  liabilityCap: '',
  consultantOptions: '',
  counterpartyOrgNr: '',
  linkedProjectId: '',
  isFrameworkContract: false,
};

const CONTRACT_AI_PROMPT = `Analysera detta avtal och returnera ENDAST ett JSON-objekt med de fält du kan identifiera. Fält som saknas eller är okända sätts till tom sträng "".

{
  "title": "",
  "contractType": "",
  "counterpartyName": "",
  "counterpartyOrgNr": "",
  "startDate": "",
  "endDate": "",
  "noticePeriod": "",
  "value": "",
  "paymentTerms": "",
  "signedDate": "",
  "projectName": "",
  "projectNumber": "",
  "consultantRole": "",
  "hourlyRate": "",
  "compensationType": "",
  "standardTerms": "",
  "indexClause": "",
  "invoiceReference": "",
  "contactPersonClient": "",
  "liabilityCap": "",
  "notes": ""
}

Datum-fält ska vara i ISO-format YYYY-MM-DD. För contractType välj ett av: Kund, Leverantör, Partner, Konsultavtal, Övrigt.
Returnera ENBART JSON, ingen annan text.`;

function statusInfo(key) {
  return STATUSES.find(s => s.key === key) || STATUSES[0];
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  return diff;
}

function DeadlineChip({ endDate, reminderDays }) {
  const days = daysUntil(endDate);
  if (days === null) return null;
  if (days < 0)   return <Chip size="small" label={`Utgick ${Math.abs(days)}d sedan`} sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontSize: '0.65rem', height: 20 }} />;
  if (days <= (reminderDays || 60)) return <Chip size="small" label={`${days}d kvar`} sx={{ bgcolor: days <= 14 ? '#FFEBEE' : '#FFF3E0', color: days <= 14 ? '#C62828' : '#E65100', fontSize: '0.65rem', height: 20, fontWeight: 700 }} />;
  return <Chip size="small" label={`${days}d kvar`} sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontSize: '0.65rem', height: 20 }} />;
}

export default function Contracts() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const role = userProfile?.role || '';
  const canAccess = ['vd', 'superadmin', 'ao_ansvarig'].includes(role);
  const [contracts, setContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);         // uppdrag från UppdragN
  const [loading, setLoading] = useState(true);
  const [importDialog, setImportDialog] = useState(false);   // fristående import-dialog
  const [importInDialog, setImportInDialog] = useState(false); // import inuti avtalsformuläret
  const [importFile, setImportFile] = useState(null);
  const [importParsing, setImportParsing] = useState(false);
  const filterStorageKey = `contracts_filters_${currentUser?.uid || 'default'}`;
  const loadSavedFilters = () => { try { return JSON.parse(localStorage.getItem(filterStorageKey) || '{}'); } catch { return {}; } };
  const [search, setSearch] = useState(() => loadSavedFilters().search || '');
  const [filterType, setFilterType] = useState(() => loadSavedFilters().filterType || '');
  const [filterStatus, setFilterStatus] = useState(() => loadSavedFilters().filterStatus || '');
  const [filterProject, setFilterProject] = useState(() => loadSavedFilters().filterProject || '');
  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sortBy, setSortBy] = useState(() => loadSavedFilters().sortBy || 'endDate');
  const [sortDir, setSortDir] = useState(() => loadSavedFilters().sortDir || 'asc');
  const [newCompanyDialog, setNewCompanyDialog] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [savingCompany, setSavingCompany] = useState(false);
  const [exportingId, setExportingId] = useState(null);
  const [previewingPDF, setPreviewingPDF] = useState(false);

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    try {
      localStorage.setItem(filterStorageKey, JSON.stringify({ search, filterType, filterStatus, filterProject, sortBy, sortDir }));
    } catch (_) {}
  }, [search, filterType, filterStatus, filterProject, sortBy, sortDir]); // eslint-disable-line

  async function load() {
    setLoading(true);
    try {
      const [contSnap, compSnap, projSnap] = await Promise.all([
        getDocs(query(collection(db, 'contracts'), orderBy('createdAt', 'desc'))),
        getDocs(query(collection(db, 'crmCompanies'), orderBy('name'))),
        getDocs(query(collection(db, 'projects'), orderBy('name'))).catch(() => ({ docs: [] })),
      ]);
      // Äldre anställningsavtal visas inte (tas bort med scripts/remove-employment-contracts.js)
      setContracts(contSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(c => c.contractType !== 'Anställning' && !c.employeeUid));
      setCompanies(compSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setProjects(projSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Contracts load error:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const data = { ...form, updatedAt: new Date() };
      if (editingId) {
        await updateDoc(doc(db, 'contracts', editingId), data);
        setContracts(prev => prev.map(c => c.id === editingId ? { id: editingId, ...data } : c));
      } else {
        data.createdBy = currentUser.uid;
        data.createdAt = new Date();
        const ref = await addDoc(collection(db, 'contracts'), data);
        setContracts(prev => [{ id: ref.id, ...data }, ...prev]);
      }
      setDialog(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, e) {
    e.stopPropagation();
    if (!window.confirm('Ta bort avtalet?')) return;
    await deleteDoc(doc(db, 'contracts', id));
    setContracts(prev => prev.filter(c => c.id !== id));
  }

  // ── Bygg sammanställnings-PDF (används av export och förhandsgranskning) ──
  async function buildAuditorPDF(contract) {
    // Hämta företagslogotyp
    let logoBase64 = null;
    let logoMime = 'image/png';
    try {
      const companyId = userProfile?.companyId;
      if (companyId) {
        const compSnap = await getDoc(doc(db, 'companies', companyId));
        const logoUrl = compSnap.data()?.logo;
        if (logoUrl) {
          const logoRes = await fetch(logoUrl);
          const logoBlob = await logoRes.blob();
          logoMime = logoBlob.type || 'image/png';
          logoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(logoBlob);
          });
        }
      }
    } catch (_) { /* logo is optional */ }

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const margin = 18;
    const headerH = 26;
    const LABEL_W = 62;
    const tableBase = {
      margin: { left: margin, right: margin },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: LABEL_W, fontStyle: 'bold', textColor: [60, 60, 60] } },
      tableLineWidth: 0.2,
    };

    pdf.setFillColor(26, 26, 26);
    pdf.rect(0, 0, pageW, headerH, 'F');

    if (logoBase64) {
      try {
        const fmt = logoMime.includes('jpeg') || logoMime.includes('jpg') ? 'JPEG' : 'PNG';
        // Measure natural dimensions to preserve aspect ratio
        const imgDims = await new Promise(resolve => {
          const img = new window.Image();
          img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
          img.onerror = () => resolve(null);
          img.src = logoBase64;
        });
        if (imgDims) {
          const maxW = 50, maxH = 20;
          const ratio = imgDims.w / imgDims.h;
          let drawW = maxW, drawH = maxW / ratio;
          if (drawH > maxH) { drawH = maxH; drawW = maxH * ratio; }
          const x = pageW - margin - drawW;
          const y = (headerH - drawH) / 2;
          pdf.addImage(logoBase64, fmt, x, y, drawW, drawH, undefined, 'FAST');
        }
      } catch (_) {}
    }
    pdf.setFontSize(12); pdf.setTextColor(255,255,255); pdf.setFont('helvetica','bold');
    pdf.text('Avtalsinformation – Revisionsexport', margin, 11);
    pdf.setFontSize(8); pdf.setFont('helvetica','normal');
    pdf.text(`Exporterad ${new Date().toLocaleDateString('sv-SE')} ${new Date().toLocaleTimeString('sv-SE',{hour:'2-digit',minute:'2-digit'})}`, margin, 19);

    let y = headerH + 8;
    pdf.setTextColor(26,26,26); pdf.setFontSize(16); pdf.setFont('helvetica','bold');
    pdf.text(contract.title || '(Namnlöst avtal)', margin, y);
    y += 8;

    const statusLabel = STATUSES.find(s => s.key === contract.status)?.label || contract.status || '';
    const subtitleParts = [
      contract.contractType,
      contract.category,
      statusLabel,
      contract.projectNumber ? `Uppdrag ${contract.projectNumber}` : '',
      contract.projectName && !contract.projectNumber ? contract.projectName : '',
    ].filter(Boolean);
    pdf.setFontSize(9); pdf.setFont('helvetica','normal'); pdf.setTextColor(100,100,100);
    pdf.text(subtitleParts.join(' · '), margin, y);
    y += 10;

    const baseRows = [
      ['Motpart', contract.counterpartyName],
      ['Typ', contract.contractType],
      ['Kategori', contract.category],
      ['Status', statusLabel],
      ['Startdatum', contract.startDate],
      ['Slutdatum', contract.endDate],
      ['Signerat datum', contract.signedDate],
      ['Undertecknat av', contract.signedBy],
      ['Uppsägningstid', contract.noticePeriod],
      ['Värde / kostnad', contract.value],
      ['Betalningsvillkor', contract.paymentTerms],
      ['Påminnelse (dagar)', contract.reminderDays ? String(contract.reminderDays) : ''],
    ].filter(r => r[1]);
    autoTable(pdf, { ...tableBase, startY: y, head: [['Fält','Värde']], body: baseRows,
      headStyles: { fillColor:[26,26,26], fontSize:8, fontStyle:'bold' },
      alternateRowStyles: { fillColor:[248,249,250] }, tableLineColor:[220,220,220] });
    y = pdf.lastAutoTable.finalY + 8;

    if (contract.contractType === 'Konsultavtal') {
      const rows = [
        ['Projektnamn', contract.projectName],
        ['Projektnummer', contract.projectNumber],
        ['Avtalstyp', contract.isFrameworkContract ? 'Ramavtal' : 'Projektavtal'],
        ['Konsultens roll', contract.consultantRole],
        ['Timpris (SEK exkl. moms)', contract.hourlyRate],
        ['Ersättningsform', contract.compensationType],
        ['Standardavtal', contract.standardTerms],
        ['Indexklausul', contract.indexClause],
        ['Fakturareferens', contract.invoiceReference],
        ['Kontaktperson beställare', contract.contactPersonClient],
        ['Ansvarsbegränsning', contract.liabilityCap],
        ['Optioner/tilläggsvillkor', contract.consultantOptions],
        ['Motpartens org.nr', contract.counterpartyOrgNr],
      ].filter(r => r[1]);
      if (rows.length) {
        if (y > 240) { pdf.addPage(); y = 20; }
        pdf.setFontSize(10); pdf.setFont('helvetica','bold'); pdf.setTextColor(2,119,189);
        pdf.text('Konsultuppgifter', margin, y); y += 4;
        autoTable(pdf, { ...tableBase, startY:y, body:rows,
          alternateRowStyles:{fillColor:[225,245,254]}, tableLineColor:[179,229,252] });
        y = pdf.lastAutoTable.finalY + 8;
      }
    }

    if (contract.notes) {
      if (y > 240) { pdf.addPage(); y = 20; }
      pdf.setFontSize(10); pdf.setFont('helvetica','bold'); pdf.setTextColor(26,26,26);
      pdf.text('Notering', margin, y); y += 5;
      pdf.setFontSize(9); pdf.setFont('helvetica','normal'); pdf.setTextColor(60,60,60);
      const lines = pdf.splitTextToSize(contract.notes, pageW - margin*2);
      pdf.text(lines, margin, y); y += lines.length * 5 + 5;
    }

    const allFiles = [
      ...(contract.files || []),
      ...(contract.fileUrl && !(contract.files||[]).some(f => f.url===contract.fileUrl)
        ? [{ name:'Avtalsdokument', url:contract.fileUrl }] : []),
    ];
    if (allFiles.length) {
      if (y > 240) { pdf.addPage(); y = 20; }
      pdf.setFontSize(10); pdf.setFont('helvetica','bold'); pdf.setTextColor(26,26,26);
      pdf.text('Bifogade dokument', margin, y); y += 4;
      autoTable(pdf, { startY:y, margin:{left:margin,right:margin},
        head:[['#','Namn','Uppladdad']],
        body: allFiles.map((f,i) => [String(i+1), f.name||'Dokument', f.uploadedAt ? new Date(f.uploadedAt).toLocaleDateString('sv-SE') : '–']),
        headStyles:{fillColor:[106,27,154],fontSize:8}, bodyStyles:{fontSize:9},
        columnStyles:{0:{cellWidth:10},2:{cellWidth:28}},
        alternateRowStyles:{fillColor:[243,229,245]}, tableLineColor:[220,220,220], tableLineWidth:0.2 });
    }

    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(7); pdf.setTextColor(170,170,170);
      pdf.text(`Sida ${i} av ${totalPages} · Lagbasen avtalsregister · Konfidentiellt`, margin, pdf.internal.pageSize.getHeight()-8);
    }

    return { pdf, allFiles };
  }

  // ── Sammanfoga sammanställnings-PDF med bifogade PDF-filer ──
  async function buildMergedPDF(contract) {
    const { pdf, allFiles } = await buildAuditorPDF(contract);
    const summaryBytes = pdf.output('arraybuffer');

    const merged = await PDFDocument.create();
    // 1. Lägg in sammanställningssidorna
    const summaryDoc = await PDFDocument.load(summaryBytes);
    const summaryPages = await merged.copyPages(summaryDoc, summaryDoc.getPageIndices());
    summaryPages.forEach(p => merged.addPage(p));

    // 2. Lägg in varje bifogad PDF
    await Promise.allSettled(
      allFiles
        .filter(f => f.url && (f.name?.toLowerCase().endsWith('.pdf') || f.url.split('?')[0].toLowerCase().endsWith('.pdf')))
        .map(async f => {
          try {
            const res = await fetch(f.url);
            const bytes = await res.arrayBuffer();
            const attachedDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
            const pages = await merged.copyPages(attachedDoc, attachedDoc.getPageIndices());
            pages.forEach(p => merged.addPage(p));
          } catch (_) { /* skip unreadable PDFs */ }
        })
    );

    return await merged.save();
  }

  async function handleExportToAuditor(contract, e) {
    e.stopPropagation();
    setExportingId(contract.id);
    try {
      const safe = (str) => (str||'').replace(/[^a-zA-Z0-9åäöÅÄÖ _-]/g,'_').slice(0,50);
      const mergedBytes = await buildMergedPDF(contract);

      const linkedProjNumber = contract.linkedProjectId
        ? projects.find(p => p.id === contract.linkedProjectId)?.projectNumber
        : null;
      const date = new Date().toISOString().slice(0, 10);
      const parts = [
        (linkedProjNumber || contract.projectNumber) ? safe(linkedProjNumber || contract.projectNumber) : null,
        safe(contract.title),
        contract.counterpartyName ? safe(contract.counterpartyName) : null,
        date,
      ].filter(Boolean);

      const blob = new Blob([mergedBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${parts.join('_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export misslyckades. Försök igen.');
    } finally {
      setExportingId(null);
    }
  }

  async function handlePreviewAuditorPDF() {
    setPreviewingPDF(true);
    try {
      // Bygg mot den sparade versionen (form = nuvarande redigeringsdata)
      const contractData = editingId ? { ...form, id: editingId } : form;
      const mergedBytes = await buildMergedPDF(contractData);
      const blob = new Blob([mergedBytes], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err) {
      console.error('Preview failed:', err);
      alert('Förhandsgranskning misslyckades.');
    } finally {
      setPreviewingPDF(false);
    }
  }

  async function handleUploadFiles(e) {
    const selected = Array.from(e.target.files || []);
    e.target.value = '';
    if (!selected.length) return;
    setUploading(true);
    try {
      const contractId = editingId || `new_${Date.now()}`;
      const uploaded = await Promise.all(selected.map(async file => {
        const fileId = Date.now().toString(36) + Math.random().toString(36).slice(2);
        const path = `contracts/${contractId}/${fileId}_${file.name}`;
        const snap = await uploadBytes(storageRef(storage, path), file);
        const url = await getDownloadURL(snap.ref);
        // Default name = filename without extension
        const defaultName = file.name.replace(/\.[^.]+$/, '');
        return { id: fileId, name: defaultName, url, storagePath: path, uploadedAt: new Date().toISOString() };
      }));
      setForm(f => ({ ...f, files: [...(f.files || []), ...uploaded] }));
    } finally {
      setUploading(false);
    }
  }

  function handleRenameFile(fileId, newName) {
    setForm(f => ({ ...f, files: f.files.map(fi => fi.id === fileId ? { ...fi, name: newName } : fi) }));
  }

  async function handleRemoveFile(fileId) {
    const file = form.files.find(f => f.id === fileId);
    if (file?.storagePath) {
      try { await deleteObject(storageRef(storage, file.storagePath)); } catch (_) {}
    }
    setForm(f => ({ ...f, files: f.files.filter(fi => fi.id !== fileId) }));
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function callHaiku(messages) {
    const data = await callClaude({ model: 'claude-haiku-4-5-20251001', max_tokens: 2000, messages });
    return JSON.parse(data.content[0].text.replace(/```json|```/g, '').trim());
  }

  async function extractContractWithAI(file) {
    if (file.type === 'application/pdf') {
      const base64 = await fileToBase64(file);
      return callHaiku([{ role: 'user', content: [
        { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
        { type: 'text', text: CONTRACT_AI_PROMPT },
      ]}]);
    } else {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return callHaiku([{ role: 'user', content: `${CONTRACT_AI_PROMPT}\n\nAvtalets text:\n${result.value}` }]);
    }
  }

  async function handleAnalyzeUploadedFiles() {
    const pdfFile = (form.files || []).find(f =>
      f.url && (f.name?.toLowerCase().endsWith('.pdf') || f.url.split('?')[0].toLowerCase().endsWith('.pdf'))
    );
    if (!pdfFile) {
      alert('Ladda upp en PDF-fil för att köra AI-analys.');
      return;
    }
    setImportParsing(true);
    try {
      const fetchRes = await fetch(pdfFile.url);
      const blob = await fetchRes.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const extracted = await callHaiku([{ role: 'user', content: [
        { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
        { type: 'text', text: CONTRACT_AI_PROMPT },
      ]}]);
      setForm(f => {
        const merged = { ...f };
        for (const [k, v] of Object.entries(extracted)) {
          if (v && (merged[k] === '' || merged[k] === undefined)) merged[k] = v;
        }
        return merged;
      });
    } catch (err) {
      console.error('AI-analys failed:', err);
      alert('AI-analys misslyckades. Försök igen.');
    } finally {
      setImportParsing(false);
    }
  }

  function openNew(type = '') {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, contractType: type });
    setImportInDialog(false);
    setImportFile(null);
    setDialog(true);
  }

  // Körs när användaren laddat upp en fil i in-dialog-importören
  async function handleImportInDialog(file) {
    if (!file) return;
    setImportParsing(true);
    setImportFile(file);
    try {
      // Upload to Storage
      const path = `contracts/import_${Date.now()}_${file.name}`;
      const snap = await uploadBytes(storageRef(storage, path), file);
      const url = await getDownloadURL(snap.ref);
      const fileId = Date.now().toString(36);
      const defaultName = file.name.replace(/\.[^.]+$/, '');
      setForm(f => ({ ...f, files: [...(f.files || []), { id: fileId, name: defaultName, url, storagePath: path, uploadedAt: new Date().toISOString() }] }));

      // AI extraction – fills empty form fields
      try {
        const extracted = await extractContractWithAI(file);
        setForm(f => {
          const merged = { ...f };
          for (const [k, v] of Object.entries(extracted)) {
            if (v && (merged[k] === '' || merged[k] === undefined)) merged[k] = v;
          }
          return merged;
        });
      } catch (aiErr) {
        console.warn('AI-extraktion misslyckades:', aiErr);
      }
    } finally {
      setImportParsing(false);
      setImportInDialog(false);
    }
  }

  // Körs från den fristående import-dialogen
  async function handleStandaloneImport() {
    if (!importFile) return;
    setImportParsing(true);
    try {
      const path = `contracts/import_${Date.now()}_${importFile.name}`;
      const snap = await uploadBytes(storageRef(storage, path), importFile);
      const url = await getDownloadURL(snap.ref);
      const fileId = Date.now().toString(36);
      const defaultName = importFile.name.replace(/\.[^.]+$/, '');

      const extracted = await extractContractWithAI(importFile);
      setEditingId(null);
      setForm({
        ...EMPTY_FORM,
        ...Object.fromEntries(Object.entries(extracted).filter(([, v]) => v !== '')),
        files: [{ id: fileId, name: defaultName, url, storagePath: path, uploadedAt: new Date().toISOString() }],
      });
      setImportDialog(false);
      setImportFile(null);
      setDialog(true);
    } catch (err) {
      console.error('Standalone import failed:', err);
      alert('Kunde inte analysera dokumentet. Försök igen eller skapa avtal manuellt.');
    } finally {
      setImportParsing(false);
    }
  }

  function openEdit(c, e) {
    e.stopPropagation();
    setEditingId(c.id);
    // Migrate legacy single fileUrl → files array
    const existingFiles = c.files || [];
    const migratedFiles = c.fileUrl && !existingFiles.some(f => f.url === c.fileUrl)
      ? [{ id: 'legacy', name: 'Avtalsdokument', url: c.fileUrl }, ...existingFiles]
      : existingFiles;
    setForm({ ...EMPTY_FORM, ...c, files: migratedFiles });
    setDialog(true);
  }

  async function handleCreateCompany() {
    if (!newCompanyName.trim()) return;
    setSavingCompany(true);
    try {
      const ref = await addDoc(collection(db, 'crmCompanies'), {
        name: newCompanyName.trim(),
        createdAt: new Date(),
        createdBy: currentUser.uid,
      });
      const newComp = { id: ref.id, name: newCompanyName.trim() };
      setCompanies(prev => [...prev, newComp].sort((a, b) => a.name.localeCompare(b.name, 'sv')));
      setForm(f => ({ ...f, counterpartyId: ref.id, counterpartyName: newCompanyName.trim() }));
      setNewCompanyDialog(false);
      setNewCompanyName('');
    } finally {
      setSavingCompany(false);
    }
  }

  function handleSort(col) {
    if (sortBy === col) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortBy(''); setSortDir('asc'); }
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  }

  const filtered = contracts.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.title?.toLowerCase().includes(q) ||
      c.counterpartyName?.toLowerCase().includes(q) || c.category?.toLowerCase().includes(q) ||
      c.projectNumber?.toLowerCase().includes(q) || c.projectName?.toLowerCase().includes(q);
    const matchType    = !filterType    || c.contractType === filterType;
    const matchStatus  = !filterStatus  || c.status === filterStatus;
    const matchProject = !filterProject || c.projectNumber?.toLowerCase().includes(filterProject.toLowerCase()) || c.projectName?.toLowerCase().includes(filterProject.toLowerCase());
    return matchSearch && matchType && matchStatus && matchProject;
  });

  const sorted = sortBy ? [...filtered].sort((a, b) => {
    let va = a[sortBy] || '', vb = b[sortBy] || '';
    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  }) : filtered;

  // Summary counts
  const expiring = contracts.filter(c => {
    const d = daysUntil(c.endDate);
    return d !== null && d >= 0 && d <= (c.reminderDays || 60);
  }).length;
  const expired  = contracts.filter(c => (daysUntil(c.endDate) ?? 1) < 0 && c.status !== 'cancelled').length;

  const COL_SX = { fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5, color: '#666', py: 1.5 };

  if (!loading && !canAccess) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Typography color="text.secondary">Åtkomst nekad – endast VD och AO.</Typography>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Top bar */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Avtalsregister</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {filtered.length} av {contracts.length} avtal
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Importera befintligt PDF- eller Word-avtal och låt AI fylla i fälten automatiskt">
            <Button variant="outlined" startIcon={<CloudUploadIcon />}
              onClick={() => setImportDialog(true)}
              sx={{ borderColor: 'rgba(255,255,255,0.4)', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              Importera
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* Digital signering – pågående arbete */}
        <Alert
          icon={<VerifiedIcon />}
          severity="info"
          sx={{ mb: 2, borderRadius: 2, bgcolor: '#e8f4fd', color: '#0d47a1',
            '& .MuiAlert-icon': { color: '#1565C0' } }}>
          <Typography variant="body2" fontWeight={600}>
            Digital signering med BankID är under utredning
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Vi utvärderar tredjepartstjänster (Scrive, Oneflow m.fl.) för att möjliggöra e-signering direkt i Lagbasen. Tills dess hanteras signering externt och signerade avtal laddas upp här.
          </Typography>
        </Alert>

        {/* Summary chips */}
        {(expiring > 0 || expired > 0) && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {expired > 0 && (
              <Chip icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                label={`${expired} avtal har gått ut`}
                size="small"
                sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 700 }}
                onClick={() => setFilterStatus('expired')} />
            )}
            {expiring > 0 && (
              <Chip icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                label={`${expiring} avtal förfaller snart`}
                size="small"
                sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700 }}
                onClick={() => setFilterStatus('expiring')} />
            )}
          </Box>
        )}

        {/* ── Snabbstart-brickor ─────────────────────────────────── */}
        <Paper elevation={0} sx={{ mb: 2.5, p: 2, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <AddIcon sx={{ fontSize: 18, color: '#FF6B00' }} />
            <Typography variant="subtitle2" fontWeight={700} color="#1a1a1a">
              Skapa nytt avtal
            </Typography>
            <Typography variant="caption" color="text.secondary">— välj avtalstyp för att komma igång</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {TYPE_TILES.map(tile => {
              const Icon = tile.icon;
              return (
                <Paper key={tile.type}
                  onClick={() => openNew(tile.type)}
                  elevation={0}
                  sx={{
                    p: tile.featured ? 2 : 1.5,
                    cursor: 'pointer', borderRadius: 2,
                    border: `${tile.featured ? 2 : 1.5}px solid ${tile.border}${tile.featured ? '66' : '33'}`,
                    bgcolor: tile.bg,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    flex: tile.featured ? '1 1 200px' : '1 1 150px',
                    maxWidth: tile.featured ? 260 : 210,
                    transition: 'box-shadow .15s, border-color .15s, transform .1s',
                    '&:hover': { boxShadow: tile.featured ? 6 : 4, borderColor: tile.color, transform: 'translateY(-1px)' },
                    '&:active': { transform: 'translateY(0)' },
                  }}>
                  <Box sx={{
                    width: tile.featured ? 42 : 36, height: tile.featured ? 42 : 36,
                    borderRadius: 1.5,
                    bgcolor: `${tile.color}22`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Icon sx={{ fontSize: tile.featured ? 24 : 20, color: tile.color }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: tile.color, lineHeight: 1.2, fontSize: tile.featured ? '0.95rem' : undefined }}>
                      {tile.type}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.3 }}>
                      {tile.desc}
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Paper>

        {/* Filter row */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Sök titel, motpart, kategori…"
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 240, bgcolor: 'white', borderRadius: 1 }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )}} />
          <TextField select size="small" label="Typ" value={filterType}
            onChange={e => setFilterType(e.target.value)}
            sx={{ minWidth: 150, bgcolor: 'white', borderRadius: 1 }}>
            <MenuItem value="">Alla typer</MenuItem>
            {CONTRACT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            sx={{ minWidth: 160, bgcolor: 'white', borderRadius: 1 }}>
            <MenuItem value="">Alla statusar</MenuItem>
            {STATUSES.map(s => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
          </TextField>
          <TextField size="small" placeholder="Uppdragsnr / projektnamn…"
            value={filterProject} onChange={e => setFilterProject(e.target.value)}
            sx={{ minWidth: 200, bgcolor: 'white', borderRadius: 1 }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <AssignmentTurnedInIcon sx={{ fontSize: 16, color: filterProject ? '#0277BD' : 'text.disabled' }} />
              </InputAdornment>
            )}} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : sorted.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <GavelIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              {contracts.length === 0
                ? 'Inga avtal ännu. Lägg till ditt första.'
                : 'Inga träffar på sökning/filter.'}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  {[
                    { col: 'title',            label: 'Avtal' },
                    { col: 'counterpartyName', label: 'Motpart' },
                    { col: 'contractType',     label: 'Typ' },
                    { col: 'category',         label: 'Kategori' },
                    { col: 'status',           label: 'Status' },
                    { col: 'startDate',        label: 'Start' },
                    { col: 'endDate',          label: 'Slutar' },
                  ].map(({ col, label }) => (
                    <TableCell key={col} sx={{ py: 1.5 }}>
                      <TableSortLabel
                        active={sortBy === col}
                        direction={sortBy === col ? sortDir : 'asc'}
                        onClick={() => handleSort(col)}
                        sx={{ ...COL_SX,
                          '&.Mui-active': { color: '#1a1a1a' },
                          '& .MuiTableSortLabel-icon': { fontSize: 14 } }}>
                        {label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell sx={COL_SX}>Dokument</TableCell>
                  <TableCell sx={{ width: 80 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map(c => {
                  const st = statusInfo(c.status);
                  return (
                    <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={e => openEdit(c, e)}>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" fontWeight={600}>{c.title}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                          {(() => {
                            const chipNr = c.linkedProjectId
                              ? projects.find(p => p.id === c.linkedProjectId)?.projectNumber
                              : c.projectNumber;
                            return chipNr ? (
                              <Chip size="small" label={chipNr}
                                sx={{ fontSize: '0.6rem', height: 17, bgcolor: '#e1f5fe', color: '#0277BD', fontWeight: 700 }} />
                            ) : null;
                          })()}
                          {c.value && (
                            <Typography variant="caption" color="text.secondary">{c.value}</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{c.counterpartyName || '–'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{c.contractType || '–'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{c.category || '–'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip size="small" label={st.label}
                            sx={{ fontSize: '0.65rem', height: 20,
                              bgcolor: st.bg, color: st.color, fontWeight: 700 }} />
                          <DeadlineChip endDate={c.endDate} reminderDays={c.reminderDays} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{c.startDate || '–'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{c.endDate || '–'}</Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const allFiles = [
                            ...(c.files || []),
                            ...(c.fileUrl && !(c.files || []).some(f => f.url === c.fileUrl)
                              ? [{ id: 'legacy', name: 'Avtalsdokument', url: c.fileUrl }] : []),
                          ];
                          if (!allFiles.length) return <Typography variant="caption" color="text.disabled">–</Typography>;
                          if (allFiles.length === 1) return (
                            <Tooltip title={allFiles[0].name || 'Öppna dokument'}>
                              <IconButton size="small" onClick={() => window.open(allFiles[0].url, '_blank')}>
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          );
                          return (
                            <Tooltip title={allFiles.map(f => f.name).join(', ')}>
                              <Chip size="small" icon={<UploadFileIcon sx={{ fontSize: 13 }} />}
                                label={`${allFiles.length} dok.`}
                                onClick={e => { e.stopPropagation(); window.open(allFiles[0].url, '_blank'); }}
                                sx={{ fontSize: '0.65rem', height: 20, cursor: 'pointer' }} />
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Exportera sammanställning till revisor (PDF)">
                            <span>
                              <IconButton size="small"
                                disabled={exportingId === c.id}
                                onClick={e => handleExportToAuditor(c, e)}
                                sx={{ opacity: 0.5, color: '#F9A825', '&:hover': { opacity: 1, color: '#F57F17' }, '&.Mui-disabled': { opacity: 0.25 } }}>
                                {exportingId === c.id
                                  ? <CircularProgress size={14} />
                                  : <FolderZipIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Redigera">
                            <IconButton size="small" onClick={e => openEdit(c, e)}
                              sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ta bort">
                            <IconButton size="small" onClick={e => handleDelete(c.id, e)}
                              sx={{ opacity: 0.3, '&:hover': { opacity: 1, color: 'error.main' } }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Dialog – nytt/redigera avtal */}
      <Dialog open={dialog} onClose={() => { setDialog(false); setEditingId(null); }}
        maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {form.contractType === 'Konsultavtal'
                ? <AssignmentTurnedInIcon sx={{ color: '#0277BD', fontSize: 28 }} />
                : <GavelIcon sx={{ color: '#FF6B00', fontSize: 28 }} />}
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {editingId
                    ? 'Redigera avtal'
                    : form.contractType === 'Konsultavtal'
                      ? 'Nytt konsultavtal'
                      : form.contractType
                        ? `Nytt ${form.contractType.toLowerCase()}savtal`
                        : 'Nytt avtal'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Fyll i det du vet – du kan komplettera senare
                </Typography>
              </Box>
            </Box>
            {/* Import-knapp – visas alltid i dialog */}
            <Tooltip title="Importera befintligt avtal (PDF/Word)">
              <Button size="small" variant="outlined" startIcon={<CloudUploadIcon />}
                onClick={() => setImportInDialog(true)}
                sx={{ flexShrink: 0, borderColor: '#FF6B00', color: '#FF6B00',
                  '&:hover': { bgcolor: '#fff8f0' } }}>
                Importera
              </Button>
            </Tooltip>
          </Box>
        </DialogTitle>
        <Divider />

        {/* ── Inline-import (visas inuti dialogen) ─────────────── */}
        {importInDialog && (
          <Box sx={{ mx: 3, mt: 2, p: 2, bgcolor: '#fff8f0', borderRadius: 2, border: '1.5px dashed #FF6B00' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoFixHighIcon sx={{ fontSize: 18, color: '#FF6B00' }} />
                <Typography variant="body2" fontWeight={700} color="#FF6B00">
                  Importera befintligt avtal
                </Typography>
              </Box>
              <Button size="small" onClick={() => { setImportInDialog(false); setImportFile(null); }}
                sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                Stäng
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Ladda upp ett PDF- eller Word-avtal. AI analyserar dokumentet och fyller i fälten automatiskt – granska och justera innan du sparar.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button component="label" variant="contained" size="small"
                startIcon={importParsing ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon />}
                disabled={importParsing}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
                {importParsing ? 'Analyserar…' : 'Välj PDF / Word'}
                <input hidden type="file" accept=".pdf,.doc,.docx"
                  onChange={e => handleImportInDialog(e.target.files?.[0])} />
              </Button>
              {importFile && !importParsing && (
                <Chip label={importFile.name} size="small" color="success"
                  onDelete={() => setImportFile(null)} />
              )}
            </Box>
            {importParsing && <LinearProgress sx={{ mt: 1.5, borderRadius: 1 }} />}
          </Box>
        )}

        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2} alignItems="flex-start">

            {/* ── Vänster kolumn: formulär ─────────────────────────── */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>

            {/* Identitet */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField label="Avtalstitel *" autoFocus
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    sx={{ flex: '2 1 260px' }} />
                  <TextField select label="Typ"
                    value={form.contractType}
                    onChange={e => setForm(f => ({ ...f, contractType: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: '1 1 140px' }}>
                    <MenuItem value=""><Typography color="text.disabled" variant="body2">Välj typ</Typography></MenuItem>
                    {CONTRACT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                  <TextField select label="Kategori"
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: '1 1 160px' }}>
                    <MenuItem value=""><Typography color="text.disabled" variant="body2">Välj kategori</Typography></MenuItem>
                    {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                  <TextField select label="Status"
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    sx={{ flex: '1 1 160px' }}>
                    {STATUSES.map(s => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
                  </TextField>
                </Box>
              </Box>
            </Grid>

            {/* Motpart */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#f0f4ff', borderRadius: 2, p: 2 }}>
                <Typography variant="caption" fontWeight="bold" color="#1565C0"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                  Motpart
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, flex: '1 1 220px' }}>
                    <TextField select label="Kopplat företag (CRM)"
                      value={form.counterpartyId}
                      onChange={e => {
                        const comp = companies.find(c => c.id === e.target.value);
                        setForm(f => ({ ...f, counterpartyId: e.target.value, counterpartyName: comp?.name || f.counterpartyName }));
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}>
                      <MenuItem value=""><Typography color="text.disabled" variant="body2">Välj ur CRM (valfritt)</Typography></MenuItem>
                      {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                    </TextField>
                    <Tooltip title="Skapa ny kund i CRM">
                      <IconButton size="small" onClick={() => setNewCompanyDialog(true)}
                        sx={{ mb: 0.5, color: '#1565C0', '&:hover': { bgcolor: '#e3f2fd' } }}>
                        <AddIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TextField label="Motpartens namn" placeholder="Om ej i CRM"
                    value={form.counterpartyName}
                    onChange={e => setForm(f => ({ ...f, counterpartyName: e.target.value }))}
                    sx={{ flex: '1 1 220px' }} />
                </Box>
              </Box>
            </Grid>

            {/* Konsultuppgifter – visas bara för typen Konsultavtal */}
            {form.contractType === 'Konsultavtal' && (
              <Grid item xs={12}>
                <Box sx={{ bgcolor: '#e1f5fe', borderRadius: 2, p: 2, border: '1px solid #0277BD' }}>
                  <Typography variant="caption" fontWeight="bold" color="#0277BD"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                    Konsultuppgifter
                  </Typography>
                  {/* Ramavtal / Projektavtal */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!form.isFrameworkContract}
                          onChange={e => setForm(f => ({ ...f, isFrameworkContract: e.target.checked }))}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#0277BD' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#0277BD' } }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {form.isFrameworkContract ? 'Ramavtal' : 'Projektavtal'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {form.isFrameworkContract ? 'Löpande samarbete utan specifikt projekt' : 'Kopplat till ett specifikt projekt'}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                  {/* Row 1: projectName, projectNumber */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField label="Projektnamn"
                      value={form.projectName}
                      onChange={e => setForm(f => ({ ...f, projectName: e.target.value }))}
                      sx={{ flex: '2 1 220px' }} />
                    <TextField label="Projektnummer"
                      value={form.projectNumber}
                      onChange={e => setForm(f => ({ ...f, projectNumber: e.target.value }))}
                      sx={{ flex: '1 1 140px' }} />
                  </Box>
                  {/* Row 2: consultantRole, counterpartyOrgNr */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField label="Konsultens roll/uppdrag"
                      value={form.consultantRole}
                      onChange={e => setForm(f => ({ ...f, consultantRole: e.target.value }))}
                      sx={{ flex: '2 1 220px' }} />
                    <TextField label="Motpartens org.nr"
                      value={form.counterpartyOrgNr}
                      onChange={e => setForm(f => ({ ...f, counterpartyOrgNr: e.target.value }))}
                      sx={{ flex: '1 1 160px' }} />
                  </Box>
                  {/* Row 3: hourlyRate, compensationType */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField label="Timpris (SEK exkl. moms)"
                      value={form.hourlyRate}
                      onChange={e => setForm(f => ({ ...f, hourlyRate: e.target.value }))}
                      sx={{ flex: '1 1 180px' }} />
                    <TextField select label="Ersättningsform"
                      value={form.compensationType}
                      onChange={e => setForm(f => ({ ...f, compensationType: e.target.value }))}
                      sx={{ flex: '1 1 160px' }}>
                      <MenuItem value="rörligt">Rörligt (löpande räkning)</MenuItem>
                      <MenuItem value="fast">Fast pris</MenuItem>
                      <MenuItem value="maxpris">Maxpris</MenuItem>
                    </TextField>
                    <TextField label="Ansvarsbegränsning"
                      placeholder="t.ex. 5 prisbasbelopp"
                      value={form.liabilityCap}
                      onChange={e => setForm(f => ({ ...f, liabilityCap: e.target.value }))}
                      sx={{ flex: '1 1 180px' }} />
                  </Box>
                  {/* Row 4: standardTerms, indexClause */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField select label="Standardavtal"
                      value={form.standardTerms}
                      onChange={e => setForm(f => ({ ...f, standardTerms: e.target.value }))}
                      sx={{ flex: '1 1 180px' }}>
                      <MenuItem value="ABK 09">ABK 09</MenuItem>
                      <MenuItem value="AMA AF Konsult 10">AMA AF Konsult 10</MenuItem>
                      <MenuItem value="Eget avtal">Eget avtal</MenuItem>
                      <MenuItem value="">Inget standardavtal</MenuItem>
                    </TextField>
                    <TextField label="Indexklausul"
                      placeholder="t.ex. KPI, fast, SSNF"
                      value={form.indexClause}
                      onChange={e => setForm(f => ({ ...f, indexClause: e.target.value }))}
                      sx={{ flex: '1 1 180px' }} />
                    <TextField label="Fakturareferens"
                      placeholder="t.ex. PO-nummer"
                      value={form.invoiceReference}
                      onChange={e => setForm(f => ({ ...f, invoiceReference: e.target.value }))}
                      sx={{ flex: '1 1 180px' }} />
                  </Box>
                  {/* Row 5: contactPersonClient */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField label="Kontaktperson hos beställare"
                      value={form.contactPersonClient}
                      onChange={e => setForm(f => ({ ...f, contactPersonClient: e.target.value }))}
                      sx={{ flex: '1 1 220px' }} />
                    <TextField label="Optioner / tilläggsvillkor"
                      placeholder="t.ex. option 6 mån"
                      value={form.consultantOptions}
                      onChange={e => setForm(f => ({ ...f, consultantOptions: e.target.value }))}
                      sx={{ flex: '1 1 220px' }} />
                  </Box>
                  {/* Row 6: linked project */}
                  {projects.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <TextField select label="Kopplat uppdrag (UppdragN)"
                        value={form.linkedProjectId}
                        onChange={e => {
                          const selectedProject = projects.find(p => p.id === e.target.value);
                          setForm(f => ({
                            ...f,
                            linkedProjectId: e.target.value,
                            projectNumber: selectedProject?.projectNumber || f.projectNumber,
                          }));
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: '1 1 260px' }}>
                        <MenuItem value=""><Typography color="text.disabled" variant="body2">Välj uppdrag (valfritt)</Typography></MenuItem>
                        {projects.map(p => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name || p.projectName || p.id}
                            {p.projectNumber ? ` (${p.projectNumber})` : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            {/* Avtalsvillkor */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: '#f0faf4', borderRadius: 2, p: 2 }}>
                <Typography variant="caption" fontWeight="bold" color="#2E7D32"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1.5 }}>
                  Avtalsvillkor
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <TextField label="Startdatum" type="date"
                    value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} />
                  <TextField label="Slutdatum" type="date"
                    value={form.endDate}
                    onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} />
                  <TextField label="Uppsägningstid" placeholder="t.ex. 3 månader"
                    value={form.noticePeriod}
                    onChange={e => setForm(f => ({ ...f, noticePeriod: e.target.value }))}
                    sx={{ flex: '1 1 150px' }} />
                  <TextField label="Påminnelse (dagar före)"
                    type="number" value={form.reminderDays}
                    onChange={e => setForm(f => ({ ...f, reminderDays: parseInt(e.target.value) || 60 }))}
                    sx={{ flex: '0 1 160px' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField label="Värde / kostnad" placeholder="t.ex. 500 KSEK/år"
                    value={form.value}
                    onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                    sx={{ flex: '1 1 180px' }} />
                  <TextField label="Betalningsvillkor" placeholder="t.ex. 30 dagar netto"
                    value={form.paymentTerms}
                    onChange={e => setForm(f => ({ ...f, paymentTerms: e.target.value }))}
                    sx={{ flex: '1 1 180px' }} />
                  <TextField label="Signerat datum" type="date"
                    value={form.signedDate}
                    onChange={e => setForm(f => ({ ...f, signedDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} />
                  <TextField label="Undertecknat av"
                    value={form.signedBy}
                    onChange={e => setForm(f => ({ ...f, signedBy: e.target.value }))}
                    sx={{ flex: '1 1 180px' }} />
                </Box>
              </Box>
            </Grid>

            {/* Notering */}
            <Grid item xs={12}>
              <TextField fullWidth label="Notering" multiline rows={4}
                placeholder="Interna anteckningar om avtalet…"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </Grid>

              </Grid>{/* /inner Grid container */}
            </Grid>{/* /left column */}

            {/* ── Höger kolumn: dokument ───────────────────────────── */}
            <Grid item xs={12} md={5}>
              <Box sx={{ bgcolor: '#f5f0ff', borderRadius: 2, p: 2, position: 'sticky', top: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="caption" fontWeight="bold" color="#6a1b9a"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Avtalsdokument
                  </Typography>
                  <Button component="label" size="small" variant="outlined"
                    startIcon={uploading ? <CircularProgress size={13} /> : <UploadFileIcon />}
                    disabled={uploading}
                    sx={{ borderColor: '#7B1FA2', color: '#7B1FA2', '&:hover': { bgcolor: '#f3e5f5' } }}>
                    {uploading ? 'Laddar upp…' : 'Bifoga filer'}
                    <input hidden type="file" accept=".pdf,.doc,.docx,.xlsx,.xls,.pptx,.ppt,.png,.jpg,.jpeg"
                      multiple onChange={handleUploadFiles} />
                  </Button>
                </Box>
                {uploading && <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />}

                {/* File list */}
                {(form.files || []).length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    Inga dokument bifogade. Klicka "Bifoga filer" för att lägga till PDF, Word, Excel m.m.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    {(form.files || []).map(fi => (
                      <Box key={fi.id} sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        bgcolor: 'white', borderRadius: 1.5, px: 1.5, py: 0.8,
                        border: '1px solid #e1d5f5',
                      }}>
                        <TextField
                          variant="standard" size="small"
                          value={fi.name}
                          onChange={e => handleRenameFile(fi.id, e.target.value)}
                          placeholder="Dokumentnamn"
                          sx={{ flex: 1, '& .MuiInput-underline:before': { borderBottom: '1px dashed #ccc' } }}
                          inputProps={{ style: { fontSize: '0.875rem' } }}
                        />
                        <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                          onClick={() => window.open(fi.url, '_blank')}
                          sx={{ color: '#7B1FA2', minWidth: 0, px: 1, fontSize: '0.72rem' }}>
                          Öppna
                        </Button>
                        <IconButton size="small" onClick={() => handleRemoveFile(fi.id)}
                          sx={{ opacity: 0.45, '&:hover': { opacity: 1, color: 'error.main' } }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* AI-analys */}
                {(form.files || []).some(f => f.name?.toLowerCase().endsWith('.pdf') || f.url?.split('?')[0].toLowerCase().endsWith('.pdf')) && (
                  <Box sx={{ mt: 1.5 }}>
                    <Divider sx={{ mb: 1.5 }} />
                    <Button fullWidth size="small" variant="outlined"
                      startIcon={importParsing ? <CircularProgress size={14} color="inherit" /> : <AutoFixHighIcon />}
                      disabled={importParsing}
                      onClick={handleAnalyzeUploadedFiles}
                      sx={{ borderColor: '#7B1FA2', color: '#7B1FA2', '&:hover': { bgcolor: '#f3e5f5' } }}>
                      {importParsing ? 'Analyserar…' : 'AI-analys'}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                      Fyller i tomma fält från uppladdad PDF
                    </Typography>
                  </Box>
                )}

                <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mt: 1.5 }}>
                  Synligt endast för Admin, VD och AO. Ej i dokumentbiblioteket.
                </Typography>
              </Box>
            </Grid>{/* /right column */}

          </Grid>{/* /outer Grid container */}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button size="large" onClick={() => { setDialog(false); setEditingId(null); }}>Avbryt</Button>
          <Button size="large" variant="outlined"
            startIcon={previewingPDF ? <CircularProgress size={16} color="inherit" /> : <FolderZipIcon />}
            disabled={previewingPDF || !form.title.trim()}
            onClick={handlePreviewAuditorPDF}
            sx={{ borderColor: '#F9A825', color: '#F57F17', '&:hover': { bgcolor: '#fffde7' } }}>
            {previewingPDF ? 'Genererar…' : 'Förhandsgranskning PDF'}
          </Button>
          <Button variant="contained" size="large" onClick={handleSave}
            disabled={saving || !form.title.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, px: 4 }}>
            {saving ? 'Sparar…' : 'Spara avtal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Skapa ny kund i CRM ──────────────────────────────────── */}
      <Dialog open={newCompanyDialog} onClose={() => { setNewCompanyDialog(false); setNewCompanyName(''); }}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessCenterIcon sx={{ color: '#1565C0', fontSize: 22 }} />
          Ny kund i CRM
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField autoFocus fullWidth label="Företagsnamn *"
            value={newCompanyName}
            onChange={e => setNewCompanyName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreateCompany(); }}
            sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setNewCompanyDialog(false); setNewCompanyName(''); }}>Avbryt</Button>
          <Button variant="contained" onClick={handleCreateCompany}
            disabled={!newCompanyName.trim() || savingCompany}
            sx={{ bgcolor: '#1565C0', '&:hover': { bgcolor: '#0d47a1' } }}>
            {savingCompany ? 'Skapar…' : 'Skapa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Import-dialog ──────────────────────────────────────────── */}
      <Dialog open={importDialog} onClose={() => { setImportDialog(false); setImportFile(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon sx={{ color: '#FF6B00', fontSize: 20 }} />
          Importera befintligt avtal med AI
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>Hur det fungerar</Typography>
            <Typography variant="caption">
              Ladda upp ett PDF- eller Word-avtal. AI:n analyserar dokumentet och försöker fylla i avtalsformulären automatiskt.
              Du granskar och justerar innan du sparar.
            </Typography>
          </Alert>

          <Box sx={{ border: '2px dashed #bdbdbd', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fafafa' }}>
            {importFile ? (
              <Box>
                <Chip label={importFile.name} onDelete={() => setImportFile(null)} sx={{ mb: 1 }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  {(importFile.size / 1024).toFixed(0)} KB
                </Typography>
              </Box>
            ) : (
              <Box>
                <CloudUploadIcon sx={{ fontSize: 40, color: '#bdbdbd', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Dra och släpp eller välj fil
                </Typography>
                <Button component="label" variant="outlined" size="small">
                  Välj PDF / Word
                  <input hidden type="file" accept=".pdf,.doc,.docx"
                    onChange={e => setImportFile(e.target.files?.[0] || null)} />
                </Button>
              </Box>
            )}
          </Box>

          {importParsing && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                AI analyserar dokumentet…
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setImportDialog(false); setImportFile(null); }}>Avbryt</Button>
          <Button variant="contained" startIcon={importParsing ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
            disabled={!importFile || importParsing}
            onClick={handleStandaloneImport}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {importParsing ? 'Analyserar…' : 'Analysera med AI'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
